import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";
import { FAQ_QUESTIONS } from "../data/faq";
import { JsonLd } from "../hooks/usePageMeta";
import { faqPageLd } from "../seo";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <PageShell testId="faq-page" padding="20px 18px 28px">
      {/* FAQPage structured data — built from the same FAQ_QUESTIONS the UI
          renders, so the schema can never drift from the visible Q&A. */}
      <JsonLd data={faqPageLd(FAQ_QUESTIONS)} />

      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <BackLink to="/" label="Home" testId="faq-back-link" />
        <AuthChip />
      </header>

      <h1
        style={{
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontStyle: "italic",
          fontSize: 36,
          margin: "0 0 8px 0",
        }}
      >
        Questions<span style={{ color: COLORS.yellow }}>.</span>
      </h1>
      <p
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          color: COLORS.faint,
          letterSpacing: "0.04em",
          margin: "0 0 28px 0",
        }}
      >
        // tap to expand
      </p>

      <div data-testid="faq-list" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {FAQ_QUESTIONS.map((item, i) => (
          <FaqRow
            key={i}
            index={i}
            question={item.q}
            answer={item.a}
            open={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
          />
        ))}
      </div>

      <AppFooter />
    </PageShell>
  );
}

const FaqRow = ({ index, question, answer, open, onToggle }) => (
  <div
    data-testid={`faq-row-${index}`}
    style={{
      borderBottom: `1px dashed ${COLORS.rule}`,
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`faq-row-toggle-${index}`}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        padding: "16px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        cursor: "pointer",
        textAlign: "left",
        color: COLORS.text,
      }}
    >
      <span
        style={{
          // Type treatment mirrored by the Privacy section headers
          // (pages/Privacy.jsx) so the two pages read as siblings.
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          letterSpacing: "0.04em",
          color: COLORS.text,
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {question}
      </span>
      <ChevronDown
        size={16}
        color={COLORS.faint}
        style={{
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 200ms ease",
          flex: "0 0 auto",
        }}
      />
    </button>
    {/* Answer is ALWAYS in the DOM (just visually collapsed when inactive)
        so it's present in the rendered/prerendered HTML — a requirement for
        Google's FAQ rich result. Visual behavior is unchanged. */}
    <div
      data-testid={`faq-row-answer-${index}`}
      aria-hidden={!open}
      style={{
        maxHeight: open ? 2000 : 0,
        opacity: open ? 1 : 0,
        overflow: "hidden",
        transition: "max-height 200ms ease, opacity 200ms ease",
      }}
    >
      <div
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 14,
          lineHeight: 1.6,
          color: COLORS.text,
          padding: "0 0 18px 0",
        }}
      >
        {answer}
      </div>
    </div>
  </div>
);
